import React, { useCallback, useEffect, useState } from 'react';
import {
  Users, RefreshCw, Save, Loader2, PlayCircle, ShieldCheck,
  Building2, CheckCircle2, XCircle, Clock, Info, PlugZap,
} from 'lucide-react';
import DirectoryTestResult from './DirectoryTestResult';
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  getTenantDirectoryConfig, updateTenantDirectoryConfig,
  triggerTenantDirectorySync, listTenantDirectoryRuns, testTenantDirectoryConnection,
} from '@/services/admin';

/**
 * DirectorySyncConfig
 * -------------------
 * Owner-level UI for pull-based directory sync. Backed by the FastAPI routes
 * defined in ``saml_routes.py`` under ``/api/saml/tenants/by-id/{id}/directory/*``.
 *
 * Three providers supported today:
 *  - **Microsoft Entra ID / Azure AD** — needs ``tenant_id``, ``client_id``, ``client_secret``
 *    (app registration with Directory.Read.All permission).
 *  - **Google Workspace** — needs a JSON service-account key, an impersonated
 *    admin subject, and the customer ID (``my_customer`` is fine).
 *  - **Okta** — needs the org URL and an API token.
 *
 * Secrets are stored server-side; the API returns ``***configured***`` back
 * for the sensitive fields so this UI shows a placeholder instead of the
 * plaintext value.
 */
const PROVIDER_FIELDS = {
  azure_ad: [
    { key: 'tenant_id', label: 'Directory (tenant) ID', placeholder: '00000000-0000-0000-0000-000000000000' },
    { key: 'client_id', label: 'Application (client) ID', placeholder: '00000000-0000-0000-0000-000000000000' },
    { key: 'client_secret', label: 'Client secret', secret: true, placeholder: 'Paste new secret to rotate' },
  ],
  google: [
    { key: 'service_account_json', label: 'Service account JSON key', secret: true, multiline: true,
      placeholder: 'Paste the full JSON contents of the service-account key' },
    { key: 'impersonated_subject', label: 'Admin subject to impersonate', placeholder: 'admin@yourdomain.com' },
    { key: 'customer', label: 'Google customer ID or domain', placeholder: 'my_customer or example.com' },
  ],
  okta: [
    { key: 'org_url', label: 'Okta org URL', placeholder: 'https://your-org.okta.com' },
    { key: 'api_token', label: 'API token', secret: true, placeholder: 'Paste new token to rotate' },
  ],
};

const DEFAULT_ROLES = [
  { id: 'viewer', label: 'Viewer' },
  { id: 'editor', label: 'Editor' },
  { id: 'admin', label: 'Admin' },
  { id: 'auditor', label: 'Auditor' },
];

const StatusChip = ({ ok, label }) => (
  <span
    className={`inline-flex items-center gap-1 text-xs rounded-full px-2 py-0.5 border ${
      ok ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
         : 'bg-rose-50 border-rose-200 text-rose-700'
    }`}
  >
    {ok ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
    {label}
  </span>
);

const RunRow = ({ run }) => {
  const ok = !run.error;
  return (
    <tr className="border-t text-xs" data-testid={`dir-run-${run.run_id || run.started_at}`}>
      <td className="py-1.5 pr-3 whitespace-nowrap">
        <StatusChip ok={ok} label={run.provider} />
      </td>
      <td className="py-1.5 pr-3 whitespace-nowrap text-slate-600">
        {run.started_at ? new Date(run.started_at).toLocaleString() : '—'}
      </td>
      <td className="py-1.5 pr-3 text-slate-700">
        {ok
          ? `+${run.users_added || 0} · ~${run.users_updated || 0} · −${run.users_deactivated || 0}`
          : (run.error || 'failed')}
      </td>
      <td className="py-1.5 text-slate-500">
        {run.total_seen != null ? `${run.total_seen} seen` : ''}
      </td>
    </tr>
  );
};

const DirectorySyncConfig = ({ tenantId }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [provider, setProvider] = useState('');
  const [defaultRole, setDefaultRole] = useState('viewer');
  const [config, setConfig] = useState({});
  const [supported, setSupported] = useState([]);
  const [lastResult, setLastResult] = useState(null);
  const [runs, setRuns] = useState([]);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const load = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const data = await getTenantDirectoryConfig(tenantId);
      setEnabled(!!data.enabled);
      setProvider(data.provider || '');
      setDefaultRole(data.default_role || 'viewer');
      setConfig(data.config || {});
      setSupported(data.supported_providers || []);
      setLastResult(data.last_result || null);
      try {
        const rs = await listTenantDirectoryRuns(tenantId);
        setRuns(Array.isArray(rs?.runs) ? rs.runs : (Array.isArray(rs) ? rs : []));
      } catch (e) { /* runs table is optional */ }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load directory sync config');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  const setField = (key, value) => setConfig((c) => ({ ...c, [key]: value }));

  const handleSave = async () => {
    if (enabled && !provider) {
      toast.error('Pick a directory provider before enabling sync');
      return;
    }
    setSaving(true);
    try {
      // Strip masked placeholders so we don't clobber stored secrets.
      const outCfg = {};
      Object.entries(config || {}).forEach(([k, v]) => {
        if (v === '***configured***') return;
        outCfg[k] = v;
      });
      const data = await updateTenantDirectoryConfig(tenantId, {
        enabled, provider: provider || null, schedule: 'nightly',
        default_role: defaultRole, config: outCfg,
      });
      toast.success('Directory sync configuration saved');
      setConfig(data.config || {});
      setLastResult(data.last_result || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save directory sync config');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await testTenantDirectoryConnection(tenantId, {
        enabled, provider, default_role: defaultRole, config,
      });
      setTestResult(res);
      if (res.ok) toast.success(`Connection OK — ${res.users_visible} users visible`);
      else toast.error('Connection test failed');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Connection test failed');
    } finally {
      setTesting(false);
    }
  };

  const handleSyncNow = async () => {
    if (!provider) {
      toast.error('Save a directory provider config before syncing');
      return;
    }
    setSyncing(true);
    try {
      const res = await triggerTenantDirectorySync(tenantId);
      if (res.ok) {
        toast.success(
          `Sync complete: +${res.users_added} added, ~${res.users_updated} updated, −${res.users_deactivated} deactivated`,
        );
      } else {
        toast.error(res.error || 'Sync failed');
      }
      setLastResult(res);
      const rs = await listTenantDirectoryRuns(tenantId);
      setRuns(Array.isArray(rs?.runs) ? rs.runs : (Array.isArray(rs) ? rs : []));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-500 py-6" data-testid="dir-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading directory sync…
      </div>
    );
  }

  const fields = PROVIDER_FIELDS[provider] || [];

  return (
    <div className="space-y-6" data-testid="directory-sync-config">
      {/* Overview + toggle */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Users className="w-4 h-4 text-indigo-600" />
            Directory sync
          </CardTitle>
          <CardDescription>
            Pull users nightly from your identity provider — new users are provisioned into
            this tenant with the default role below, and users removed from your directory are
            deactivated locally (records are kept for audit).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between rounded-lg border p-3 bg-slate-50/50">
            <div>
              <div className="text-sm font-medium">Enable nightly sync</div>
              <p className="text-xs text-slate-500">
                Runs on the platform scheduler. Use <em>Sync now</em> to trigger immediately.
              </p>
            </div>
            <Switch
              checked={enabled}
              onCheckedChange={setEnabled}
              data-testid="dir-enable-switch"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Provider</Label>
              <Select value={provider} onValueChange={(v) => { setProvider(v); setConfig({}); setTestResult(null); }}>
                <SelectTrigger data-testid="dir-provider-select">
                  <SelectValue placeholder="Choose provider" />
                </SelectTrigger>
                <SelectContent>
                  {(supported.length ? supported : [
                    { id: 'azure_ad', name: 'Microsoft Entra ID / Azure AD' },
                    { id: 'google',   name: 'Google Workspace' },
                    { id: 'okta',     name: 'Okta' },
                  ]).map((p) => (
                    <SelectItem key={p.id} value={p.id} data-testid={`dir-provider-${p.id}`}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Default role for new users</Label>
              <Select value={defaultRole} onValueChange={setDefaultRole}>
                <SelectTrigger data-testid="dir-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DEFAULT_ROLES.map((r) => (
                    <SelectItem key={r.id} value={r.id}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Provider credentials */}
      {provider && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-emerald-600" />
              {supported.find((p) => p.id === provider)?.name || provider} credentials
            </CardTitle>
            <CardDescription>
              Secrets are encrypted at rest. Existing secrets are masked as
              <Badge variant="outline" className="mx-1">***configured***</Badge>
              — leave those fields blank to keep the stored value, or paste a new value to rotate.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {fields.map((f) => (
              <div key={f.key} data-testid={`dir-field-${f.key}`}>
                <Label>{f.label}{f.secret ? <span className="text-slate-400"> (secret)</span> : null}</Label>
                {f.multiline ? (
                  <Textarea
                    rows={8}
                    value={config[f.key] === '***configured***' ? '' : (config[f.key] || '')}
                    onChange={(e) => setField(f.key, e.target.value)}
                    placeholder={config[f.key] === '***configured***' ? '***configured*** — paste new to rotate' : f.placeholder}
                    className="font-mono text-xs"
                  />
                ) : (
                  <Input
                    type={f.secret ? 'password' : 'text'}
                    value={config[f.key] === '***configured***' ? '' : (config[f.key] || '')}
                    onChange={(e) => setField(f.key, e.target.value)}
                    placeholder={config[f.key] === '***configured***' ? '***configured*** — paste new to rotate' : f.placeholder}
                  />
                )}
              </div>
            ))}
            {provider === 'azure_ad' && (
              <div className="text-xs text-slate-500 flex gap-2 items-start bg-blue-50 border border-blue-200 rounded p-2">
                <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                Grant the app registration <b>Directory.Read.All</b> (Application permission) and
                admin-consent it. Beskar reads users from <code>/v1.0/users</code>.
              </div>
            )}
            {provider === 'google' && (
              <div className="text-xs text-slate-500 flex gap-2 items-start bg-blue-50 border border-blue-200 rounded p-2">
                <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                Enable domain-wide delegation on the service account and authorise the OAuth scope
                <code className="mx-1">https://www.googleapis.com/auth/admin.directory.user.readonly</code>
                in the Google Workspace Admin console. Impersonate a super-admin.
              </div>
            )}
            {provider === 'okta' && (
              <div className="text-xs text-slate-500 flex gap-2 items-start bg-blue-50 border border-blue-200 rounded p-2">
                <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                Create a read-only API token from <b>Security → API → Tokens</b>. The token needs
                access to <code className="break-all">/api/v1/users</code>.
              </div>
            )}
            <div className="flex flex-wrap items-center gap-3 pt-1">
              <Button type="button" variant="secondary" onClick={handleTest} disabled={testing}
                data-testid="dir-test-connection-btn">
                {testing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Testing…</>
                         : <><PlugZap className="w-4 h-4 mr-2" />Test connection</>}
              </Button>
              <span className="text-xs text-slate-500">Uses the values above (saved or not). Nothing is synced.</span>
            </div>
            <DirectoryTestResult result={testResult} />
          </CardContent>
        </Card>
      )}

      {/* Last sync result */}
      {lastResult && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Clock className="w-4 h-4 text-slate-500" />
              Last sync result
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2 text-xs" data-testid="dir-last-result">
              <StatusChip ok={!!lastResult.ok} label={lastResult.ok ? 'succeeded' : 'failed'} />
              <Badge variant="outline">{lastResult.provider}</Badge>
              <Badge variant="outline">+{lastResult.users_added || 0} added</Badge>
              <Badge variant="outline">~{lastResult.users_updated || 0} updated</Badge>
              <Badge variant="outline">−{lastResult.users_deactivated || 0} deactivated</Badge>
              {lastResult.departments_added ? (
                <Badge variant="outline" className="gap-1"><Building2 className="w-3 h-3" />{lastResult.departments_added} depts</Badge>
              ) : null}
              {lastResult.error && <span className="text-rose-600">{lastResult.error}</span>}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent runs */}
      {runs.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Recent runs</CardTitle>
            <CardDescription>Last 20 nightly + manual pulls.</CardDescription>
          </CardHeader>
          <CardContent>
            <table className="w-full text-left">
              <thead>
                <tr className="text-xs text-slate-500">
                  <th className="pb-2 pr-3">Provider</th>
                  <th className="pb-2 pr-3">Started</th>
                  <th className="pb-2 pr-3">Delta (add · update · deactivate)</th>
                  <th className="pb-2">Volume</th>
                </tr>
              </thead>
              <tbody>
                {runs.map((r) => <RunRow key={r.run_id || r.started_at} run={r} />)}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Action bar */}
      <div className="flex flex-wrap items-center justify-end gap-2 sticky bottom-0 bg-white/90 backdrop-blur border-t py-3 -mx-6 px-6">
        <Button type="button" variant="outline" onClick={load} data-testid="dir-reload-btn">
          <RefreshCw className="w-4 h-4 mr-2" />Reload
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={handleSyncNow}
          disabled={syncing || !provider}
          data-testid="dir-sync-now-btn"
        >
          {syncing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Syncing…</>
                   : <><PlayCircle className="w-4 h-4 mr-2" />Sync now</>}
        </Button>
        <Button type="button" onClick={handleSave} disabled={saving} data-testid="dir-save-btn">
          {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</>
                  : <><Save className="w-4 h-4 mr-2" />Save directory config</>}
        </Button>
      </div>
    </div>
  );
};

export default DirectorySyncConfig;
